import { useState } from "react";
import { Icon } from "./Icon";
import { AN_CARD, AN_H, AN_NUM_SM, AN_SUB, AN_T, Ring, Seg, Unit } from "./analytics";
import { pdHeat, pdInsight, pdSort, penetration, pickDropCsv } from "../../lib/msdAnalytics";

// Service load vs P&D: of the job cards opened, how many chose pick & drop when
// booked. The server sends counts per workshop; every percentage, order and flag
// here is worked out from them.

const TYPES = [
  ["free", "Free"],
  ["pms", "PMS"],
  ["both", "Free + PMS"],
  ["compare", "Compare"],
];
const TILES = [
  ["free", "Free service"],
  ["pms", "Paid service (PMS)"],
  ["both", "Free and PMS"],
];
const TYPE_LABEL = { free: "Free service", pms: "Paid service (PMS)", both: "Free and PMS" };

const num = (x) => Number(x || 0).toLocaleString("en-IN");
const pctText = (v) => (v == null ? "–" : `${v}%`);

const TH = "whitespace-nowrap border-0 border-b border-solid border-msd-line px-3 py-[10px] text-left text-[12.5px] font-semibold text-msd-muted";
const TD = "whitespace-nowrap border-0 border-b border-solid border-msd-line px-3 py-[10px]";
const NUM = "text-right tabular-nums";

const sumRows = (rows, view) =>
  rows.reduce((a, r) => ({ jc: a.jc + r[view].jc, pd: a.pd + r[view].pd }), { jc: 0, pd: 0 });

function Bar({ pct, avg }) {
  return (
    <span className="relative block h-2 rounded-full bg-[rgba(120,120,128,.14)]">
      <span className="absolute inset-y-0 left-0 rounded-full bg-[var(--pd-fill)]" style={{ width: `${pct || 0}%` }} />
      {avg != null && (
        <span
          className="absolute -inset-y-1 w-[2px] rounded-[2px] bg-[#F79009]"
          style={{ left: `${avg}%` }}
          title={`Group average ${avg}%`}
        />
      )}
    </span>
  );
}

function Heat({ v }) {
  return (
    <span className="inline-block min-w-[52px] rounded-lg px-2 py-1 text-center font-semibold" style={pdHeat(v)}>
      {pctText(v)}
    </span>
  );
}

// One table row: a workshop, a zone (tone "zone") or the group total (tone "total").
function Row({ name, sub, counts, view, compare, avg, tone, low, rowCounts }) {
  const { jc, pd } = counts;
  const v = penetration(pd, jc);
  const cls =
    tone === "total"
      ? "font-bold"
      : tone === "zone"
        ? "bg-msd-soft font-semibold"
        : jc
          ? ""
          : "text-msd-faint";
  const head = (
    <th scope="row" className={`${TD} text-left ${tone === "total" ? "border-b-0 border-t" : ""} ${tone ? "font-[inherit]" : "font-medium"}`}>
      <span className="flex items-center gap-2">
        {name}
        {sub && <span className={`${AN_SUB} font-normal`}>{sub}</span>}
        {low && (
          <span className="inline-flex text-[#F79009]" title="Below group average" role="img" aria-label="Below group average">
            <Icon name="warn" size={12} stroke={2.4} />
          </span>
        )}
      </span>
    </th>
  );
  const edge = tone === "total" ? "border-b-0 border-t" : "";
  if (compare) {
    return (
      <tr className={cls}>
        {head}
        <td className={`${TD} ${NUM} ${edge}`}>{num(jc)}</td>
        {["free", "pms", "both"].map((t) => {
          const c = rowCounts[t];
          const p = penetration(c.pd, c.jc);
          return (
            <td key={t} className={`${TD} ${edge}`}>
              {tone ? <b>{pctText(p)}</b> : <Heat v={p} />}
            </td>
          );
        })}
      </tr>
    );
  }
  return (
    <tr className={cls}>
      {head}
      <td className={`${TD} ${NUM} ${edge}`}>{num(jc)}</td>
      <td className={`${TD} ${NUM} ${edge}`}>{num(pd)}</td>
      <td className={`${TD} w-[40%] ${edge}`}>
        {tone === "total" ? null : jc ? <Bar pct={v} avg={avg} /> : <span className={AN_SUB}>No job cards</span>}
      </td>
      <td className={`${TD} ${NUM} w-14 font-semibold ${edge}`}>{pctText(v)}</td>
    </tr>
  );
}

export function ServiceLoadCard({ data, scopeLabel, workshopLabel = (x) => x, onExport }) {
  const [type, setType] = useState("both");
  const [sort, setSort] = useState("volume");
  const compare = type === "compare";
  const view = compare ? "both" : type;
  const insight = pdInsight(data, view);
  const avg = insight.pct;
  const zoned = data.zones.length > 0;

  // Rows arrive grouped by zone; start a new block wherever the zone changes.
  const blocks = [];
  for (const r of data.rows) {
    const last = blocks[blocks.length - 1];
    if (last && last.zone === r.zone) last.rows.push(r);
    else blocks.push({ zone: r.zone, rows: [r] });
  }
  const rowCounts = (rows) => ({ free: sumRows(rows, "free"), pms: sumRows(rows, "pms"), both: sumRows(rows, "both") });

  const sentence = (
    <>
      {insight.best && insight.worst ? (
        <>
          {insight.best.zone} is at <b>{insight.best.pct}%</b> and {insight.worst.zone} at <b>{insight.worst.pct}%</b>.
        </>
      ) : avg != null ? (
        <>
          The group is at <b>{avg}%</b>.
        </>
      ) : (
        <>No job cards in this period.</>
      )}
      {insight.none.names.length > 0 && (
        <>
          {" "}
          {insight.none.names.length} workshop{insight.none.names.length === 1 ? "" : "s"} had no pick &amp; drop across{" "}
          {num(insight.none.jobCards)} job cards: {insight.none.names.map(workshopLabel).join(", ")}.
        </>
      )}
    </>
  );

  return (
    <section className={AN_CARD} data-testid="msd-service-load">
      <div className={`${AN_H} !items-center`}>
        <div>
          <div className={AN_T}>Service load vs P&amp;D</div>
          <div className={AN_SUB}>Job cards where the customer chose pick &amp; drop · {scopeLabel}</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Seg label="Service type" value={type} onChange={setType} options={TYPES} />
          <button
            type="button"
            onClick={onExport}
            data-testid="msd-service-load-export"
            className="inline-flex h-[38px] items-center gap-[7px] rounded-xl border-0 bg-msd-surface px-[14px] text-[14px] font-semibold text-msd-ink shadow-msd-e1 hover:bg-msd-soft"
          >
            <Icon name="doc" size={16} />
            Export
          </button>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-3 max-[760px]:grid-cols-1">
        {TILES.map(([t, label]) => {
          const c = data.total[t];
          const p = penetration(c.pd, c.jc);
          const on = type === t;
          return (
            <button
              key={t}
              type="button"
              aria-pressed={on}
              onClick={() => setType(t)}
              data-testid={`msd-service-load-tile-${t}`}
              className={`flex cursor-pointer items-center justify-between gap-[10px] rounded-2xl border-0 px-4 py-[14px] text-left font-[inherit] text-[inherit] hover:bg-msd-surface hover:shadow-msd-e2 ${
                on ? "bg-msd-surface shadow-[inset_0_0_0_2px_var(--royal)]" : "bg-msd-soft"
              }`}
            >
              <span className="min-w-0">
                <span className={`${AN_SUB} block`}>{label}</span>
                <span className={`${AN_NUM_SM} block`}>
                  {p == null ? "–" : p}
                  {p == null ? null : <Unit>%</Unit>}
                </span>
                <span className={AN_SUB}>
                  {num(c.pd)} of {num(c.jc)} job cards
                </span>
              </span>
              <svg width="44" height="44" viewBox="0 0 44 44" aria-hidden="true">
                <Ring cx={22} r={17} w={6} frac={(p || 0) / 100} color="var(--royal)" />
              </svg>
            </button>
          );
        })}
      </div>

      <div className="mt-[14px] flex items-start gap-2 rounded-xl bg-msd-soft px-3 py-[10px] text-[13.5px] text-msd-ink2" data-testid="msd-service-load-insight">
        <span className="mt-px shrink-0 text-[#3A5F94]">
          <Icon name="info" size={16} stroke={2} />
        </span>
        <span>{sentence}</span>
      </div>

      <div className="mx-[2px] mb-2 mt-4 flex flex-wrap items-center justify-between gap-2">
        <span className={AN_SUB}>{compare ? "Darker blue means more customers chose pick & drop" : TYPE_LABEL[view]}</span>
        <div className="flex items-center gap-2">
          <span className={AN_SUB}>Sort</span>
          <Seg
            label="Sort"
            value={sort}
            onChange={setSort}
            options={[
              ["volume", "Job cards"],
              ["pen", "Penetration"],
            ]}
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[620px] border-separate border-spacing-0 text-[14.5px]">
          <thead>
            <tr>
              <th className={TH}>Workshop</th>
              <th className={`${TH} ${NUM}`}>Job cards</th>
              {compare ? (
                <>
                  <th className={`${TH} ${NUM}`}>Free</th>
                  <th className={`${TH} ${NUM}`}>PMS</th>
                  <th className={`${TH} ${NUM}`}>Free + PMS</th>
                </>
              ) : (
                <>
                  <th className={`${TH} ${NUM}`}>Pick &amp; drop</th>
                  <th className={TH}>
                    Penetration <span className={`${AN_SUB} font-normal`}>· line = group average</span>
                  </th>
                  <th className={`${TH} ${NUM}`}>%</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {blocks.map((b) => {
              const rows = pdSort(b.rows, view, sort);
              return [
                zoned && (
                  <Row
                    key={`zone-${b.zone || "none"}`}
                    name={b.zone || "Other workshops"}
                    sub={`${b.rows.length} workshop${b.rows.length === 1 ? "" : "s"}`}
                    counts={sumRows(b.rows, view)}
                    rowCounts={rowCounts(b.rows)}
                    view={view}
                    compare={compare}
                    avg={avg}
                    tone="zone"
                  />
                ),
                ...rows.map((r) => {
                  const v = penetration(r[view].pd, r[view].jc);
                  return (
                    <Row
                      key={r.location}
                      name={workshopLabel(r.location)}
                      counts={r[view]}
                      rowCounts={r}
                      view={view}
                      compare={compare}
                      avg={avg}
                      low={!compare && v != null && avg != null && v < avg}
                    />
                  );
                }),
              ];
            })}
            <Row name="Group total" counts={data.total[view]} rowCounts={data.total} view={view} compare={compare} avg={avg} tone="total" />
          </tbody>
        </table>
      </div>

      {data.not_recorded > 0 && (
        <div className={`${AN_SUB} mt-3`}>
          {data.not_recorded} {data.not_recorded === 1 ? "car has" : "cars have"} no pick &amp; drop choice recorded (bookings made
          before it was asked). They count as job cards, not as pick &amp; drop.
        </div>
      )}
    </section>
  );
}

export const downloadServiceLoadCsv = (data, name) => {
  const blob = new Blob([pickDropCsv(data)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
};
